// src/components/Apm/Quote/Quote.js

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { FastField, Field, FieldArray, Form, Formik, setNestedObjectValues, useFormikContext } from 'formik';

import * as Yup from 'yup';
import i18n from 'i18next';

import { toast } from 'react-toastify';

import { batch, useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import {
  Badge,
  Box,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { alpha, styled } from '@mui/material/styles';

import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import SaveIcon from '@mui/icons-material/Save';

import { setItemForEdit } from '../../../redux/slices/itemForEditSlice';

import { setPopup, setPopupInv2, setPopupQuote } from '../../../redux/slices/popupSlice';

import { setFormTouchedQuote } from '../../../redux/slices/formTouchedSlice';

import {
  useCreateQuoteMutation,
  useDeleteQuoteMutation,
  useUpdateQuoteMutation,
} from '../../../redux/features/quoteApi';

import { useGetInvsQuery } from '../../../redux/features/invApi';

import { useRetrieveUserQuery } from '../../../redux/features/authApi';

import { useGetImageQuery } from '../../../redux/features/imageApi';

import Autocomplete from '../../Controls/FormsUI/AutocompleteWrapper';
import AutocompleteItemForItem from '../../Controls/FormsUI/AutocompleteItemForItem';

import ButtonAction from '../../Controls/Buttons/ButtonAction';
import ButtonBasic from '../../Controls/Buttons/ButtonBasic';
import ButtonDelete from '../../Controls/Buttons/ButtonDelete';

import InputArray from '../../Controls/FormsUI/InputArray';
import InputArrayNumber from '../../Controls/FormsUI/InputArrayNumber';
import SelectBasic from '../../Controls/FormsUI/SelectBasic';

import DesktopDatePickerBasic from '../../Controls/Inputs/DesktopDatePickerBasic';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import TextFieldRegular from '../../Controls/Inputs/TextFieldRegular';
import TooltipBasic from '../../Controls/Inputs/TooltipBasic';

import Popup from '../../Controls/Popups/Popup';
import PopupInv2 from '../../Controls/Popups/PopupInv2';

import Contact from '../Contact/Contact';
import Person from '../Person/Person';
import ItemInv from '../ItemInv/ItemInv';
import ContractTerm from '../Term/ContractTerm';
import TermOfPayment from '../Term/TermOfPayment';

import EmailBasic from '../Email/EmailBasic';
import Entries from '../Load/Entries';
import LoadDetails from '../Load/LoadDetails';
import Inv from '../Inv/Inv';

import QuotePdf from './QuotePdf/QuotePdf';

import { initialValuesContact } from '../Tables/InitialValues/initialValuesContact';

import { initialValuesContractTerms } from '../Tables/InitialValues/initialValuesContractTerms';

import { initialValuesInv } from '../Tables/InitialValues/initialValuesInv';

import { initialValuesItemInv } from '../Tables/InitialValues/initialValuesItemInv';

import { initialValuesPerson } from '../Tables/InitialValues/initialValuesPerson';

import { initialValuesTermOfPayment } from '../Tables/InitialValues/initialValuesTermOfPayment';

import { maxNumberAllowedPerSubscription } from '../../Data/generalData';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const MENU_ITEM_HEIGHT = 48;

const SUBSCRIPTION_LIMIT_INDEXES = {
  invoice: {
    active_free: 6,
    active_pro: 7,
    active_premium: 8,
  },

  client: {
    active_free: 9,
    active_pro: 10,
    active_premium: 11,
  },

  invoiceItem: {
    active_free: 12,
    active_pro: 13,
    active_premium: 14,
  },
};

// -----------------------------------------------------------------------------
// Styled components
// -----------------------------------------------------------------------------

const MenuStyled = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(0),
    minWidth: 180,

    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],

    boxShadow:
      'rgb(255, 255, 255) 0 0 0 0, ' +
      'rgba(0, 0, 0, 0.05) 0 0 0 1px, ' +
      'rgba(0, 0, 0, 0.1) 0 10px 15px -3px, ' +
      'rgba(0, 0, 0, 0.05) 0 4px 6px -2px',

    '& .MuiMenu-list': {
      padding: '4px 0',
    },

    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },

      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const TypographyPopover = styled(Typography)(({ theme }) => ({
  padding: 16,
  maxWidth: 350,
  color: 'rgba(0, 0, 0, 0.87)',
  fontSize: theme.typography.pxToRem(20),
}));

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

const validationSchema = Yup.object({
  bill_to: Yup.object()
    .nullable()
    .typeError(() => i18n.t('yup.required'))
    .required(() => i18n.t('yup.required')),

  person: Yup.object().nullable(),

  mode: Yup.object().nullable(),

  status: Yup.object().nullable(),

  qn: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.invoice_number_max_length'))
    .positive(() => i18n.t('yup.invoice_number_should_be_positive'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),

  date_inv: Yup.date().required(() => i18n.t('yup.required')),

  currency: Yup.object()
    .nullable()
    .typeError(() => i18n.t('yup.required'))
    .required(() => i18n.t('yup.required')),

  load_detail: Yup.string()
    .max(60, () => i18n.t('yup.max_60_characters'))
    .nullable(),

  load_address: Yup.string()
    .max(30, () => i18n.t('yup.max_30_characters'))
    .nullable(),

  unload_address: Yup.string()
    .max(30, () => i18n.t('yup.max_30_characters'))
    .nullable(),

  iteminv_invs: Yup.array().of(
    Yup.object({
      item_for_item_inv: Yup.object()
        .nullable()
        .typeError(() => i18n.t('yup.required'))
        .required(() => i18n.t('yup.required')),
    })
  ),

  entry_invs: Yup.array().of(
    Yup.object({
      country_load: Yup.object().nullable(),

      country_unload: Yup.object().nullable(),

      zip_load: Yup.string()
        .max(20, () => i18n.t('yup.max_20_characters'))
        .nullable(),

      zip_unload: Yup.string()
        .max(20, () => i18n.t('yup.max_20_characters'))
        .nullable(),

      city_load: Yup.string()
        .max(50, () => i18n.t('yup.max_50_characters'))
        .nullable(),

      city_unload: Yup.string()
        .max(50, () => i18n.t('yup.max_50_characters'))
        .nullable(),

      dangerousgoods_class: Yup.string()
        .max(30, () => i18n.t('yup.max_30_characters'))
        .nullable(),

      temp_control_details: Yup.string()
        .max(40, () => i18n.t('yup.max_40_characters'))
        .nullable(),
    })
  ),

  comment_invs: Yup.array().of(
    Yup.object({
      comment: Yup.string()
        .max(400, () => i18n.t('yup.max_400_characters'))
        .nullable(),
    })
  ),
});

// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

const isDirectImageUrl = (value) => {
  return typeof value === 'string' && /^(https?:\/\/|data:|blob:|\/)/i.test(value);
};

const resolveImageUrl = (value) => {
  if (!value) {
    return null;
  }

  if (isDirectImageUrl(value)) {
    return value;
  }

  if (typeof value !== 'object') {
    return null;
  }

  return value.s3_url ?? value.url ?? value.image ?? value.file ?? null;
};

const getImageQueryArg = (value) => {
  if (!value || resolveImageUrl(value)) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return String(value.id ?? value.uf ?? value.unique_field ?? '');
};

const normalizeQuote = (value) => {
  const source = value && typeof value === 'object' ? value : {};

  const { note_terms: initialPaymentTerm, quote: _initialLegacyQuote, ...baseValues } = initialValuesInv;

  const { note_terms: sourcePaymentTerm, quote: _sourceLegacyQuote, ...sourceValues } = source;

  return {
    ...baseValues,
    ...sourceValues,

    bill_to: source.bill_to ?? null,

    person: source.person ?? null,

    payment_term: source.payment_term ?? sourcePaymentTerm ?? initialPaymentTerm ?? null,

    contract_terms: source.contract_terms ?? null,

    currency: source.currency ?? null,

    status: source.status ?? null,

    iteminv_invs: Array.isArray(source.iteminv_invs) ? source.iteminv_invs : (baseValues.iteminv_invs ?? []),

    entry_invs: Array.isArray(source.entry_invs) ? source.entry_invs : (baseValues.entry_invs ?? []),

    comment_invs: Array.isArray(source.comment_invs) ? source.comment_invs : (baseValues.comment_invs ?? []),

    history_invs: Array.isArray(source.history_invs) ? source.history_invs : [],

    is_quote: true,
  };
};

const getRelationUf = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'object') {
    return value.uf ?? null;
  }

  return value;
};

const getCurrencyCode = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'object') {
    return value.currency_code ?? value.currencycode ?? null;
  }

  return value;
};

const toUtcMidnightIso = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

const hasEntryDetails = (quote) => {
  const firstDetails = quote?.entry_invs?.[0]?.entrydetails?.[0];

  if (!firstDetails || typeof firstDetails !== 'object') {
    return false;
  }

  return Object.values(firstDetails).some((value) => value !== null && value !== undefined && value !== '');
};

const calculateTotals = (items) => {
  const rows = Array.isArray(items) ? items : [];

  return rows.reduce(
    (result, item) => {
      const amount = Number(item?.amount) || 0;

      const quantity = Number(item?.quantity) || 1;

      const vat = Number(item?.item_for_item_inv?.vat) || 0;

      const subtotal = amount * quantity;

      const vatAmount = subtotal * (vat / 100);

      return {
        subtotal: result.subtotal + subtotal,

        vatTotal: result.vatTotal + vatAmount,

        total: result.total + subtotal + vatAmount,
      };
    },
    {
      subtotal: 0,
      vatTotal: 0,
      total: 0,
    }
  );
};

const isSubscriptionLimitReached = ({ resource, subscriptionType, currentCount }) => {
  const index = SUBSCRIPTION_LIMIT_INDEXES[resource]?.[subscriptionType];

  if (typeof index !== 'number') {
    return false;
  }

  const limit = Number(maxNumberAllowedPerSubscription[index]);

  if (!Number.isFinite(limit)) {
    return false;
  }

  return currentCount >= limit;
};

const getPopupTitle = ({ fieldName, t }) => {
  switch (fieldName) {
    case 'bill_to':
      return t('popup.title.client_details');

    case 'person':
      return t('popup.title.contact_person');

    case 'payment_term':
      return t('popup.title.payment_term');

    case 'contract_terms':
      return t('popup.title.invoice_terms');

    case 'email':
      return t('popup.title.email_basic');

    case 'iteminv_invs':
      return t('popup.title.invoice_item');

    default:
      return '';
  }
};

// -----------------------------------------------------------------------------
// Formik observer
// -----------------------------------------------------------------------------

const FormikBridge = ({ valuesRef, onDirtyChange }) => {
  const { values, dirty } = useFormikContext();

  valuesRef.current = values;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

// -----------------------------------------------------------------------------
// Reusable form layout
// -----------------------------------------------------------------------------

const FormRow = ({ label, children, labelMd = 5.5, fieldMd = 6.5, spacing = 1 }) => (
  <Grid container item xs={12} spacing={spacing}>
    <Grid item xs={12} md={labelMd}>
      <Typography variant="h6" textAlign="start" noWrap>
        {label}
      </Typography>
    </Grid>

    <Grid
      item
      xs={12}
      md={fieldMd}
      sx={{
        minWidth: 0,
      }}
    >
      {children}
    </Grid>
  </Grid>
);

const EditableField = ({ children, onEdit, iconButtonSx, disabled = false, ariaLabel }) => (
  <Box
    sx={{
      display: 'flex',
      width: '100%',
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
      }}
    >
      {children}
    </Box>

    <IconButton
      type="button"
      color="primary"
      disabled={disabled}
      aria-label={ariaLabel}
      sx={iconButtonSx}
      onClick={onEdit}
    >
      <ModeEditOutlineIcon fontSize="small" />
    </IconButton>
  </Box>
);

// -----------------------------------------------------------------------------
// Invoice item section
// -----------------------------------------------------------------------------

const QuoteItemsSection = ({
  values,
  errors,
  setFieldValue,
  mdMediaDown,
  smMediaDown,
  t,
  disabledAddButtonItem,
  itemLimitAnchor,
  onItemLimitOpen,
  onItemLimitClose,
  openInPopupAutoField,
}) => {
  const rows = Array.isArray(values.iteminv_invs) ? values.iteminv_invs : [];

  return (
    <FieldArray name="iteminv_invs">
      {(arrayHelpers) => (
        <>
          {!mdMediaDown ? (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Grid
                  container
                  sx={{
                    mt: 1,
                    mb: 1,
                  }}
                >
                  <Grid
                    item
                    md={6.5}
                    xs={12}
                    sx={{
                      bgcolor: '#ccc',
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        ml: 1.5,
                        color: 'black',
                        fontWeight: 600,
                      }}
                    >
                      {t('inv.description')}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    md={1}
                    sx={{
                      bgcolor: '#ccc',
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        ml: 1.5,
                        color: 'black',
                        fontWeight: 600,
                      }}
                    >
                      {t('inv.quantity')}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    md={1.5}
                    sx={{
                      bgcolor: '#ccc',
                    }}
                  >
                    <Typography
                      sx={{
                        ml: 1.5,
                        color: 'black',
                        fontWeight: 600,
                      }}
                    >
                      {t('inv.rate')}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    md={1}
                    sx={{
                      bgcolor: '#ccc',
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        ml: 1.5,
                        color: 'black',
                        fontWeight: 600,
                      }}
                    >
                      {t('inv.vat_%')}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    md={2}
                    sx={{
                      bgcolor: '#ccc',
                    }}
                  >
                    <Typography
                      align="right"
                      sx={{
                        mr: 2,
                        color: 'black',
                        fontWeight: 600,
                      }}
                    >
                      {t('inv.amount')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  minWidth: 31.8,
                }}
              />
            </Box>
          ) : (
            <Typography
              variant="h6"
              textAlign="start"
              sx={{
                mb: 1.5,
              }}
            >
              {t('general.goods_services_capital')}
            </Typography>
          )}

          {rows.map((item, index) => {
            const amount = Number(item?.amount) || 0;

            const quantity = Number(item?.quantity) || 1;

            const vat = Number(item?.item_for_item_inv?.vat) || 0;

            const articleTotal = Math.round(amount * quantity * (1 + vat / 100) * 100 + Number.EPSILON) / 100;

            return (
              <Box
                key={item?.id ?? `quote-item-${index}`}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: mdMediaDown ? 'block' : 'flex',

                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12} md={6.5}>
                        <EditableField
                          iconButtonSx={{
                            mt: mdMediaDown ? 2 : -1,
                          }}
                          onEdit={(event) => {
                            if (disabledAddButtonItem && !item.item_for_item_inv) {
                              onItemLimitOpen(event);
                              return;
                            }

                            openInPopupAutoField(item.item_for_item_inv, 'iteminv_invs', index);
                          }}
                        >
                          <AutocompleteItemForItem
                            name={`iteminv_invs[${index}].item_for_item_inv`}
                            label={mdMediaDown ? t('inv.description') : null}
                          />
                        </EditableField>
                      </Grid>

                      <Grid item xs={12} md={1}>
                        <InputArrayNumber
                          name={`iteminv_invs[${index}].quantity`}
                          type="number"
                          label={mdMediaDown ? t('inv.quantity') : null}
                        />
                      </Grid>

                      <Grid item xs={12} md={1.5}>
                        <InputArrayNumber
                          name={`iteminv_invs[${index}].amount`}
                          type="number"
                          label={mdMediaDown ? t('inv.rate') : null}
                        />
                      </Grid>

                      <Grid item xs={12} md={1}>
                        <Field
                          component={InputArray}
                          name={`iteminv_invs[${index}].item_for_item_inv.vat`}
                          type="number"
                          disabled
                          label={mdMediaDown ? t('inv.vat_%') : null}
                        />
                      </Grid>

                      <Grid
                        container
                        item
                        xs={12}
                        md={2}
                        sx={{
                          mt: mdMediaDown ? 0 : -1,
                        }}
                      >
                        <Field
                          component={TextFieldRegular}
                          name={`iteminv_invs[${index}].article_total`}
                          type="number"
                          label={mdMediaDown ? t('inv.total') : null}
                          value={articleTotal || ''}
                          inputProps={{
                            style: {
                              textAlign: mdMediaDown ? 'start' : 'end',
                            },
                          }}
                          onChange={(event) => {
                            const enteredTotal = Number(event.target.value) || 0;

                            const baseAmount = enteredTotal / (1 + vat / 100) / quantity;

                            void setFieldValue(`iteminv_invs[${index}].amount`, baseAmount);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box
                    sx={{
                      alignSelf: 'start',
                    }}
                  >
                    <ButtonAction
                      type="button"
                      size="small"
                      color="secondary"
                      variant={mdMediaDown ? 'contained' : 'text'}
                      fullWidth={mdMediaDown}
                      disabled={rows.length < 2}
                      sx={{
                        mb: mdMediaDown ? 2 : 2.5,

                        ...(mdMediaDown
                          ? {}
                          : {
                              minWidth: 32,
                              maxWidth: 32,
                              minHeight: 20,
                              maxHeight: 20,
                            }),
                      }}
                      onClick={() => {
                        arrayHelpers.remove(index);
                      }}
                    >
                      <RemoveIcon />
                    </ButtonAction>
                  </Box>
                </Box>

                {mdMediaDown && rows.length !== index + 1 && (
                  <Divider
                    variant="middle"
                    sx={{
                      mb: 2,
                    }}
                  />
                )}
              </Box>
            );
          })}

          <ButtonBasic
            type="button"
            color="primary"
            variant="outlined"
            fullWidth={smMediaDown}
            disabled={rows.length >= 5}
            onClick={() => {
              arrayHelpers.push({
                item_for_item_inv: null,
                description: '',
                quantity: null,
                amount: null,
                vat: null,
              });
            }}
          >
            <Typography noWrap variant="button">
              {t('button.add_line_item')}
            </Typography>
          </ButtonBasic>

          <Popover
            open={Boolean(itemLimitAnchor)}
            anchorEl={itemLimitAnchor}
            onClose={onItemLimitClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <TypographyPopover>
              {t('toast.need_to_upgrade_in_order_to_register_new_items_for_item_inv')}
            </TypographyPopover>
          </Popover>
        </>
      )}
    </FieldArray>
  );
};

// -----------------------------------------------------------------------------
// Comments
// -----------------------------------------------------------------------------

const QuoteCommentsSection = ({ values, errors, smMediaDown, t }) => (
  <FieldArray name="comment_invs">
    {(arrayHelpers) => (
      <>
        {(values.comment_invs ?? []).map((comment, index) => (
          <Grid item xs={12} key={comment?.id ?? `quote-comment-${index}`}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                <FastField
                  component={InputArray}
                  name={`comment_invs[${index}].comment`}
                  label={t(smMediaDown ? 'quote.note_to_quote_short' : 'quote.note_to_quote')}
                  type="text"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  helperText={errors.comment_invs?.[index]?.comment}
                />
              </Box>

              <IconButton
                type="button"
                color="secondary"
                onClick={() => {
                  arrayHelpers.remove(index);
                }}
              >
                <RemoveIcon />
              </IconButton>
            </Box>
          </Grid>
        ))}

        <Grid item xs={12}>
          <ButtonBasic
            type="button"
            size="small"
            color="primary"
            variant="outlined"
            fullWidth={smMediaDown}
            disabled={(values.comment_invs ?? []).length >= 3}
            onClick={() => {
              arrayHelpers.push({
                comment: '',
              });
            }}
          >
            <Typography noWrap variant="button">
              {t('button.add_note_to_invoice')}
            </Typography>
          </ButtonBasic>
        </Grid>
      </>
    )}
  </FieldArray>
);

// -----------------------------------------------------------------------------
// Totals
// -----------------------------------------------------------------------------

const QuoteTotals = ({ totals, t }) => (
  <Grid
    container
    sx={{
      mb: 1,
    }}
  >
    <Grid item xs={0} md={4} />

    <Grid
      item
      xs={7}
      md={5}
      sx={{
        pr: 1,
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h6" align="right">
          {t('inv.sub_total')}
        </Typography>

        <Typography variant="h6" align="right">
          {t('inv.vat')}
        </Typography>

        <Divider variant="middle" />

        <Typography variant="h6" align="right" fontWeight={700}>
          {t('inv.total')}
        </Typography>
      </Stack>
    </Grid>

    <Grid item xs={5} md={2.5}>
      <Stack
        spacing={2}
        sx={{
          mr: 2,
        }}
      >
        <Typography variant="h6" align="right">
          {totals.subtotal.toFixed(2)}
        </Typography>

        <Typography variant="h6" align="right">
          {totals.vatTotal.toFixed(2)}
        </Typography>

        <Divider variant="middle" />

        <Typography variant="h6" align="right" fontWeight={700}>
          {totals.total.toFixed(2)}
        </Typography>
      </Stack>
    </Grid>
  </Grid>
);

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const Quote = forwardRef(({ initialValue = initialValuesInv, onSaved, onDeleted }, ref) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const theme = useTheme();

  const smallMedia = useMediaQuery('(max-width:1075px)');

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const mdMediaDown = useMediaQuery(theme.breakpoints.down('md'));

  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));

  const formikRef = useRef(null);

  const valuesRef = useRef(normalizeQuote(initialValue));

  const initialQuote = useMemo(() => normalizeQuote(initialValue), [initialValue]);

  const [invoiceDraft, setInvoiceDraft] = useState(null);

  const [showPdf, setShowPdf] = useState(false);

  const [checked, setChecked] = useState(false);

  const [checkedEntries, setCheckedEntries] = useState(false);

  const [invoicePdfLang, setInvoicePdfLang] = useState('');

  const [clientLimitAnchor, setClientLimitAnchor] = useState(null);

  const [itemLimitAnchor, setItemLimitAnchor] = useState(null);

  const [personWarningAnchor, setPersonWarningAnchor] = useState(null);

  const [historyAnchor, setHistoryAnchor] = useState(null);

  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);

  const [emailMenuAnchor, setEmailMenuAnchor] = useState(null);

  const [invoiceLimitAnchor, setInvoiceLimitAnchor] = useState(null);

  const { popup = false, popupInv2 = false } = useSelector((state) => state.popupSlice ?? {});

  const { contacts = [] } = useSelector((state) => state.contactSlice ?? {});

  const { itemsForItemInv = [] } = useSelector((state) => state.itemForItemInvSlice ?? {});

  const { itemForEdit = null } = useSelector((state) => state.itemForEditSlice ?? {});

  const { data: account } = useRetrieveUserQuery();

  const { data: invoicePage } = useGetInvsQuery({
    quote: false,
    limit: 1,
  });

  const company = account?.company ?? null;

  const subscriptionType = account?.subscription_type ?? null;

  const logoValue = company?.logo ?? null;

  const logoQueryArg = getImageQueryArg(logoValue);

  const { data: logoResponse } = useGetImageQuery(logoQueryArg, {
    skip: !logoQueryArg,
  });

  const logo = resolveImageUrl(logoValue) ?? resolveImageUrl(logoResponse) ?? null;

  const [createQuote, { isLoading: isCreating }] = useCreateQuoteMutation();

  const [updateQuote, { isLoading: isUpdating }] = useUpdateQuoteMutation();

  const [deleteQuote, { isLoading: isDeleting }] = useDeleteQuoteMutation();

  const isSaving = isCreating || isUpdating;

  const disabledAddButtonInv = isSubscriptionLimitReached({
    resource: 'invoice',
    subscriptionType,
    currentCount: invoicePage?.count ?? 0,
  });

  const disabledAddButtonClient = isSubscriptionLimitReached({
    resource: 'client',
    subscriptionType,
    currentCount: contacts.length,
  });

  const disabledAddButtonItemInv = isSubscriptionLimitReached({
    resource: 'invoiceItem',
    subscriptionType,
    currentCount: itemsForItemInv.length,
  });

  const freeAppMssg = subscriptionType === 'active_free' ? t('note.quote_created_with_online_app_cargos_md') : '';

  useEffect(() => {
    valuesRef.current = initialQuote;

    setChecked(Boolean(initialQuote.load_detail || initialQuote.load_address || initialQuote.unload_address));

    setCheckedEntries(hasEntryDetails(initialQuote));

    setShowPdf(false);
    setInvoicePdfLang('');
  }, [initialQuote]);

  const closePopovers = useCallback(() => {
    setClientLimitAnchor(null);
    setItemLimitAnchor(null);
    setPersonWarningAnchor(null);
    setHistoryAnchor(null);
    setInvoiceLimitAnchor(null);
  }, []);

  const handleDirtyChange = useCallback(
    (dirty) => {
      dispatch(setFormTouchedQuote(dirty));
    },
    [dispatch]
  );

  const openInPopupAutoField = useCallback(
    (item, fieldName, index = null) => {
      let fields;

      switch (fieldName) {
        case 'bill_to':
          fields = item ?? initialValuesContact;
          break;

        case 'person':
          fields = item ?? initialValuesPerson;
          break;

        case 'payment_term':
          fields = item ?? initialValuesTermOfPayment;
          break;

        case 'contract_terms':
          fields = item ?? initialValuesContractTerms;
          break;

        case 'iteminv_invs':
          fields = item ?? initialValuesItemInv;
          break;

        default:
          fields = item ?? {};
      }

      batch(() => {
        dispatch(
          setItemForEdit({
            ...fields,
            fieldName,
            index,
          })
        );

        dispatch(setPopup(true));
      });
    },
    [dispatch]
  );

  const addOrEditAutoField = useCallback(
    (value) => {
      const formik = formikRef.current;

      if (!formik || !itemForEdit) {
        return;
      }

      if (itemForEdit.fieldName === 'iteminv_invs') {
        const index = itemForEdit.index;

        if (index !== null && index !== undefined) {
          void formik.setFieldValue(`iteminv_invs[${index}].item_for_item_inv`, value);
        }
      } else if (itemForEdit.fieldName === 'person') {
        const persons = value?.contact_persons ?? [];

        const personIndex = itemForEdit.index;

        formik.setValues({
          ...formik.values,

          bill_to: value,

          person:
            personIndex === null || personIndex === undefined
              ? (persons.at(-1) ?? null)
              : (persons[personIndex] ?? null),
        });
      } else {
        void formik.setFieldValue(itemForEdit.fieldName, value);
      }

      batch(() => {
        dispatch(setPopup(false));
        dispatch(setItemForEdit(null));
      });
    },
    [dispatch, itemForEdit]
  );

  const deleteAutoField = useCallback(
    (id) => {
      const formik = formikRef.current;

      if (!formik || !itemForEdit) {
        return;
      }

      if (itemForEdit.fieldName === 'iteminv_invs') {
        const nextItems = (formik.values.iteminv_invs ?? []).filter((item) => item?.item_for_item_inv?.id !== id);

        void formik.setFieldValue('iteminv_invs', nextItems);
      } else if (itemForEdit.fieldName === 'person') {
        const billTo = formik.values.bill_to;

        const remainingPersons = (billTo?.contact_persons ?? []).filter((person) => person.id !== id);

        formik.setValues({
          ...formik.values,

          bill_to: billTo
            ? {
                ...billTo,
                contact_persons: remainingPersons,
              }
            : null,

          person: null,
        });
      } else {
        void formik.setFieldValue(itemForEdit.fieldName, null);
      }

      dispatch(setItemForEdit(null));
    },
    [dispatch, itemForEdit]
  );

  const buildQuotePayload = useCallback(
    (values) => ({
      qn: values.qn || null,

      date_inv: values.date_inv || null,

      date_due: values.date_due || null,

      bill_to: getRelationUf(values.bill_to),

      person: getRelationUf(values.person),

      payment_term: getRelationUf(values.payment_term),

      contract_terms: getRelationUf(values.contract_terms),

      currency: getCurrencyCode(values.currency),

      status: values.status ?? null,

      load: null,

      load_detail: checked ? (values.load_detail ?? '') : '',

      load_address: checked ? (values.load_address ?? '') : '',

      unload_address: checked ? (values.unload_address ?? '') : '',

      entry_invs: checkedEntries
        ? (values.entry_invs ?? []).map((entry) => ({
            ...entry,

            date_load: toUtcMidnightIso(entry.date_load),

            date_unload: toUtcMidnightIso(entry.date_unload),
          }))
        : [],

      iteminv_invs: Array.isArray(values.iteminv_invs) ? values.iteminv_invs : [],

      comment_invs: Array.isArray(values.comment_invs) ? values.comment_invs : [],

      mode: null,
      incoterm: null,

      is_quote: true,
    }),
    [checked, checkedEntries]
  );

  const showSaveError = useCallback(
    (error) => {
      const status = error?.status ?? error?.originalStatus ?? error?.response?.status;

      const responseData = JSON.stringify(error?.data ?? error?.response?.data ?? '');

      if (status === 402) {
        toast.error(() => t('toast.need_to_upgrade_in_order_to_register_new_quotes'), {
          autoClose: 4000,
        });

        return;
      }

      if (responseData.includes('not unique') || responseData.includes('unique constraint')) {
        toast.error(() => t('toast.quote_with_this_number_exist'));

        return;
      }

      toast.error(() => t('toast.not_saved_try_again'));
    },
    [t]
  );

  const resetQuoteEditor = useCallback((nextValues = normalizeQuote(initialValuesInv)) => {
    valuesRef.current = nextValues;

    formikRef.current?.resetForm({
      values: nextValues,
    });

    setChecked(Boolean(nextValues.load_detail || nextValues.load_address || nextValues.unload_address));

    setCheckedEntries(hasEntryDetails(nextValues));

    setShowPdf(false);
    setInvoicePdfLang('');
  }, []);

  const saveQuote = useCallback(
    async ({ values, closeAfterSave }) => {
      if (!values || isSaving) {
        return null;
      }

      const wasUpdate = Boolean(values.id);

      const payload = buildQuotePayload(values);

      try {
        const response = wasUpdate
          ? await updateQuote({
              id: values.id,
              body: payload,
            }).unwrap()
          : await createQuote(payload).unwrap();

        const savedQuote = normalizeQuote(response);

        dispatch(setFormTouchedQuote(false));

        onSaved?.(savedQuote);

        toast.success(
          `${t('toast.quote_capital')} # ${savedQuote.qn ?? ''} ${
            wasUpdate ? t('toast.successfully_updated') : t('toast.successfully_created')
          }`
        );

        if (closeAfterSave) {
          resetQuoteEditor();

          dispatch(setPopupQuote(false));

          return savedQuote;
        }

        resetQuoteEditor(savedQuote);

        return savedQuote;
      } catch (error) {
        console.error('Quote save failed:', error);

        showSaveError(error);

        return null;
      }
    },
    [buildQuotePayload, createQuote, dispatch, isSaving, onSaved, resetQuoteEditor, showSaveError, t, updateQuote]
  );

  const handleDelete = useCallback(
    async (_event, id) => {
      if (!id || isDeleting) {
        toast.error(() => t('toast.quote_could_not_be_found'));

        return;
      }

      const quoteNumber = valuesRef.current?.qn ?? '';

      try {
        await deleteQuote(id).unwrap();

        resetQuoteEditor();

        batch(() => {
          dispatch(setPopupQuote(false));

          dispatch(setFormTouchedQuote(false));
        });

        onDeleted?.(id);

        toast.success(`${t('toast.quote_capital')} # ${quoteNumber} ${t('toast.successfully_deleted')}`);
      } catch (error) {
        console.error('Quote delete failed:', error);

        toast.error(() => t('toast.quote_could_not_be_deleted'));
      }
    },
    [deleteQuote, dispatch, isDeleting, onDeleted, resetQuoteEditor, t]
  );

  const validateAndRun = useCallback(
    async (validateForm, setTouched, callback) => {
      const errors = await validateForm();

      if (Object.keys(errors).length > 0) {
        await setTouched(setNestedObjectValues(errors, true));

        toast.error(() => t('toast.correct_errors_on_page'));

        return;
      }

      await callback();
    },
    [t]
  );

  const generatePdfDocument = useCallback((language) => {
    setInvoicePdfLang(language);
    setShowPdf(true);
    setPdfMenuAnchor(null);
  }, []);

  const emailPdfDocument = useCallback(
    (values, total, language) => {
      batch(() => {
        dispatch(
          setItemForEdit({
            ...values,
            fieldName: 'email',
            totalInvoice: total,
          })
        );

        dispatch(setPopup(true));
      });

      setInvoicePdfLang(language);
      setEmailMenuAnchor(null);
    },
    [dispatch]
  );

  const openInvoiceFromQuote = useCallback(
    (event, values) => {
      event.preventDefault();

      const today = new Date().toISOString().split('T')[0];

      const invoiceItems = (values.iteminv_invs ?? []).map(({ id, ...item }) => item);

      const invoiceComments = (values.comment_invs ?? []).map(({ id, ...comment }) => comment);

      setInvoiceDraft({
        ...values,

        id: undefined,
        uf: undefined,

        qn: undefined,
        vn: '',

        date_inv: today,

        entry_invs: [],
        history_invs: [],

        iteminv_invs: invoiceItems,

        comment_invs: invoiceComments,

        status: null,

        is_quote: false,
      });

      dispatch(setPopupInv2(true));
    },
    [dispatch]
  );

  useImperativeHandle(
    ref,
    () => ({
      handleSubmit: () => formikRef.current?.submitForm(),
    }),
    []
  );

  const popupTitle = getPopupTitle({
    fieldName: itemForEdit?.fieldName,
    t,
  });

  return (
    <>
      <Container
        maxWidth={false}
        disableGutters={lgMediaDown}
        sx={{
          width: '100%',
        }}
      >
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialQuote}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          onSubmit={(values) =>
            saveQuote({
              values,
              closeAfterSave: true,
            })
          }
        >
          {({ values, errors, handleBlur, validateForm, setTouched, setFieldValue }) => {
            const totals = calculateTotals(values.iteminv_invs);

            const totalInvoice = totals.total.toFixed(2);

            return (
              <Form autoComplete="off">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    mt: 2,
                    mb: 1,
                  }}
                >
                  {/* Header */}

                  <Grid
                    container
                    item
                    xs={12}
                    spacing={1}
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Grid item xs={12} md={6}>
                      <Typography variant="h4">{t(smMediaDown ? 'quote.quote_short' : 'quote.quote')}</Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {Object.keys(errors).length > 0 && (
                        <Typography variant="h6" color="error">
                          {t('yup.fields_color_red_are_mandatory')}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {/* Customer and status */}

                  <Grid container item xs={12} md={7.7} spacing={1}>
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12} md={8}>
                        <FormRow label={t('quote.quote_to')} labelMd={4.35} fieldMd={7.65}>
                          <EditableField
                            iconButtonSx={{
                              mt: -1,
                            }}
                            onEdit={(event) => {
                              if (disabledAddButtonClient && !values.bill_to) {
                                setClientLimitAnchor(event.currentTarget);

                                return;
                              }

                              openInPopupAutoField(values.bill_to, 'bill_to');
                            }}
                          >
                            <Autocomplete name="bill_to" onBlur={handleBlur} />
                          </EditableField>
                        </FormRow>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <FormRow label={t('quote.contact_person')} labelMd={4.35} fieldMd={7.65}>
                          <EditableField
                            iconButtonSx={{
                              mt: -1,
                            }}
                            onEdit={(event) => {
                              if (!values.bill_to?.id) {
                                setPersonWarningAnchor(event.currentTarget);

                                return;
                              }

                              const index = values.person
                                ? (values.bill_to?.contact_persons ?? []).findIndex(
                                    (person) => person.id === values.person.id
                                  )
                                : null;

                              openInPopupAutoField(values.person, 'person', index);
                            }}
                          >
                            <Autocomplete name="person" personsArray={values.bill_to?.contact_persons ?? []} />
                          </EditableField>
                        </FormRow>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={8}
                        sx={{
                          mt: 2,
                        }}
                      >
                        <FormRow label={t('quote.status')} labelMd={4.35} fieldMd={7.65}>
                          <Box
                            sx={{
                              display: 'flex',
                              width: '100%',
                            }}
                          >
                            <Box
                              sx={{
                                flexGrow: 1,
                                minWidth: 0,
                              }}
                            >
                              <SelectBasic name="status" quoteorinv="0" />
                            </Box>

                            <TooltipBasic title={t('notification.history')} placement="bottom-start">
                              <IconButton
                                id="quote-history-button"
                                type="button"
                                onClick={(event) => {
                                  setHistoryAnchor(event.currentTarget);
                                }}
                              >
                                <Badge badgeContent={values.history_invs?.length ?? 0}>
                                  <AutoAwesomeMotionIcon />
                                </Badge>
                              </IconButton>
                            </TooltipBasic>
                          </Box>
                        </FormRow>
                      </Grid>
                    </Grid>
                  </Grid>

                  {mdMediaDown && (
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                  )}

                  {/* Quote details */}

                  <Grid container item xs={12} md={4.3} spacing={1.5}>
                    <FormRow label={t('quote.quote_hash_short')}>
                      <FastField
                        as={TextFieldNormal}
                        name="qn"
                        type="number"
                        placeholder={t('inv.number_will_be_generated')}
                      />
                    </FormRow>

                    <FormRow label={t('quote.quote_date')}>
                      <DesktopDatePickerBasic name="date_inv" />
                    </FormRow>

                    <FormRow label={t('quote.valid_until')}>
                      <DesktopDatePickerBasic name="date_due" />
                    </FormRow>

                    <FormRow label={t('quote.payment_term')}>
                      <EditableField
                        iconButtonSx={{
                          mt: -1,
                        }}
                        onEdit={() => {
                          openInPopupAutoField(values.payment_term, 'payment_term');
                        }}
                      >
                        <Autocomplete name="payment_term" />
                      </EditableField>
                    </FormRow>

                    <FormRow label={t('inv.currency_asterisk')}>
                      <SelectBasic name="currency" />
                    </FormRow>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Loading/unloading */}

                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        label={t('button.include_details_loading_unloading')}
                        componentsProps={{
                          typography: {
                            variant: 'button',
                            color: 'primary',
                          },
                        }}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => {
                              setChecked((current) => !current);
                            }}
                          />
                        }
                      />
                    </FormGroup>
                  </Grid>

                  {checked && (
                    <Grid container item xs={12} spacing={1}>
                      <LoadDetails />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Entries */}

                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        label={t('button.include_load_details')}
                        componentsProps={{
                          typography: {
                            variant: 'button',
                            color: 'primary',
                          },
                        }}
                        control={
                          <Checkbox
                            checked={checkedEntries}
                            onChange={() => {
                              setCheckedEntries((current) => !current);
                            }}
                          />
                        }
                      />
                    </FormGroup>
                  </Grid>

                  {checkedEntries && (
                    <FieldArray name="entry_invs">
                      {(arrayHelpers) => (
                        <Grid container item xs={12}>
                          <Entries
                            entries={values.entry_invs ?? []}
                            arrayHelpers={arrayHelpers}
                            openInPopupAutoField={openInPopupAutoField}
                          />
                        </Grid>
                      )}
                    </FieldArray>
                  )}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Products/services */}

                  <Grid item xs={12}>
                    <QuoteItemsSection
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      mdMediaDown={mdMediaDown}
                      smMediaDown={smMediaDown}
                      t={t}
                      disabledAddButtonItem={disabledAddButtonItemInv}
                      itemLimitAnchor={itemLimitAnchor}
                      onItemLimitOpen={(event) => {
                        setItemLimitAnchor(event.currentTarget);
                      }}
                      onItemLimitClose={() => {
                        setItemLimitAnchor(null);
                      }}
                      openInPopupAutoField={openInPopupAutoField}
                    />
                  </Grid>

                  {/* Totals */}

                  <Grid item xs={12}>
                    <QuoteTotals totals={totals} t={t} />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Notes */}

                  <Grid container item xs={12} spacing={1}>
                    <QuoteCommentsSection values={values} errors={errors} smMediaDown={smMediaDown} t={t} />
                  </Grid>

                  {/* Contract terms */}

                  <Grid item xs={12}>
                    <EditableField
                      iconButtonSx={{
                        mt: 2.5,
                      }}
                      onEdit={() => {
                        openInPopupAutoField(values.contract_terms, 'contract_terms');
                      }}
                    >
                      <Autocomplete name="contract_terms" label={t('quote.quote_terms')} />
                    </EditableField>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Actions */}

                  <Grid
                    item
                    xs={12}
                    sx={{
                      my: 3,
                    }}
                  >
                    <Stack direction={smallMedia ? 'column' : 'row'} spacing={2}>
                      <Stack
                        direction={mdMediaDown ? 'column' : 'row'}
                        spacing={2}
                        sx={{
                          flexGrow: 1,
                        }}
                      >
                        <ButtonBasic
                          type="button"
                          variant="outlined"
                          fullWidth={mdMediaDown}
                          disabled={isSaving || isDeleting}
                          startIcon={<SaveAsIcon />}
                          onClick={() => {
                            validateAndRun(validateForm, setTouched, () => formikRef.current?.submitForm());
                          }}
                        >
                          <Typography variant="button">{t('button.save_and_close')}</Typography>
                        </ButtonBasic>

                        <ButtonBasic
                          type="button"
                          variant="outlined"
                          fullWidth={mdMediaDown}
                          disabled={isSaving || isDeleting}
                          startIcon={<SaveIcon />}
                          onClick={() => {
                            validateAndRun(validateForm, setTouched, () =>
                              saveQuote({
                                values,
                                closeAfterSave: false,
                              })
                            );
                          }}
                        >
                          <Typography variant="button">{t('button.save')}</Typography>
                        </ButtonBasic>

                        <ButtonDelete
                          id={values.id}
                          type="button"
                          variant="outlined"
                          color="secondary"
                          fullWidth={mdMediaDown}
                          disabled={isSaving || isDeleting || !values.id}
                          handleDelete={handleDelete}
                        >
                          <Typography variant="button">{t('button.delete')}</Typography>
                        </ButtonDelete>
                      </Stack>

                      <Stack direction={mdMediaDown ? 'column' : 'row'} spacing={2}>
                        <ButtonBasic
                          id="quote-email-button"
                          type="button"
                          variant="outlined"
                          color="success"
                          fullWidth={mdMediaDown}
                          startIcon={<ForwardToInboxIcon />}
                          endIcon={<KeyboardArrowDownIcon />}
                          disabled={!values.id}
                          onClick={(event) => {
                            setEmailMenuAnchor(event.currentTarget);
                          }}
                        >
                          <Typography variant="button">{t('button.email')}</Typography>
                        </ButtonBasic>

                        <ButtonBasic
                          id="quote-pdf-button"
                          type="button"
                          variant="outlined"
                          color={showPdf ? 'error' : 'success'}
                          fullWidth={mdMediaDown}
                          startIcon={<PictureAsPdfIcon />}
                          endIcon={<KeyboardArrowDownIcon />}
                          disabled={!values.id}
                          onClick={(event) => {
                            setPdfMenuAnchor(event.currentTarget);
                          }}
                        >
                          <Typography variant="button">
                            {t(showPdf ? 'general.download' : 'button.generate')}
                          </Typography>
                        </ButtonBasic>

                        <ButtonBasic
                          type="button"
                          variant="outlined"
                          color="warning"
                          fullWidth={mdMediaDown}
                          disabled={!values.id}
                          startIcon={<ReceiptLongIcon />}
                          onClick={(event) => {
                            if (disabledAddButtonInv) {
                              setInvoiceLimitAnchor(event.currentTarget);

                              return;
                            }

                            openInvoiceFromQuote(event, values);
                          }}
                        >
                          <Typography variant="button">{t('button.create')}</Typography>
                        </ButtonBasic>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Form observer */}

                <FormikBridge valuesRef={valuesRef} onDirtyChange={handleDirtyChange} />

                {/* History */}

                <Menu
                  id="quote-history-menu"
                  anchorEl={historyAnchor}
                  open={Boolean(historyAnchor)}
                  onClose={() => {
                    setHistoryAnchor(null);
                  }}
                  PaperProps={{
                    elevation: 2,

                    sx: {
                      mt: 2,

                      maxHeight: MENU_ITEM_HEIGHT * 9.5,

                      minWidth: 300,
                      maxWidth: 650,

                      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.32))',
                    },
                  }}
                  transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top',
                  }}
                  anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom',
                  }}
                >
                  {(values.history_invs ?? []).length === 0 ? (
                    <MenuItem>
                      <Typography noWrap>{t('notification.no_history')}</Typography>
                    </MenuItem>
                  ) : (
                    <MenuItem>
                      <Typography fontWeight="bold">{t('notification.history')}</Typography>
                    </MenuItem>
                  )}

                  {(values.history_invs ?? [])
                    .slice()
                    .reverse()
                    .map((history, index) => {
                      const changedBy = history.changed_by;

                      const changedByName = changedBy
                        ? [changedBy.first_name, changedBy.last_name].filter(Boolean).join(' ') || changedBy.email || ''
                        : '';

                      return (
                        <MenuItem key={history.id ?? `quote-history-${index}`}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              minWidth: 0,
                            }}
                          >
                            <Box>
                              {new Date(history.date_registered).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}{' '}
                              {history.action ? t(`status.${history.action}`) : ''}
                            </Box>

                            <Box fontWeight="bold">{history.status ? t(`status.${history.status}`) : ''}</Box>

                            <Box
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {changedByName}
                            </Box>
                          </Stack>
                        </MenuItem>
                      );
                    })}
                </Menu>

                {/* Email menu */}

                <MenuStyled
                  id="quote-email-menu"
                  anchorEl={emailMenuAnchor}
                  open={Boolean(emailMenuAnchor)}
                  onClose={() => {
                    setEmailMenuAnchor(null);
                  }}
                >
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      validateAndRun(validateForm, setTouched, () => emailPdfDocument(values, totalInvoice, 'RO/RU'));
                    }}
                  >
                    <PictureAsPdfIcon />

                    <Typography>{t('general.ro_ru')}</Typography>
                  </MenuItem>

                  <MenuItem
                    disableRipple
                    onClick={() => {
                      validateAndRun(validateForm, setTouched, () => emailPdfDocument(values, totalInvoice, 'EN'));
                    }}
                  >
                    <PictureAsPdfIcon />

                    <Typography>{t('general.en')}</Typography>
                  </MenuItem>
                </MenuStyled>

                {/* PDF menu */}

                <MenuStyled
                  id="quote-pdf-menu"
                  anchorEl={pdfMenuAnchor}
                  open={Boolean(pdfMenuAnchor)}
                  onClose={() => {
                    setPdfMenuAnchor(null);
                  }}
                >
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      if (!showPdf) {
                        validateAndRun(validateForm, setTouched, () => generatePdfDocument('RO/RU'));
                      }
                    }}
                  >
                    {!showPdf && (
                      <>
                        <PictureAsPdfIcon />

                        <Typography>{t('general.ro_ru')}</Typography>
                      </>
                    )}

                    {showPdf && invoicePdfLang === 'RO/RU' && (
                      <QuotePdf
                        company={company}
                        account={account}
                        values={values}
                        logo={logo}
                        update
                        setShowPdf={setShowPdf}
                        showPdf={showPdf}
                        invoicePdfLang={invoicePdfLang}
                        checked={checked}
                        checkedEntries={checkedEntries}
                        freeAppMssg={freeAppMssg}
                      />
                    )}
                  </MenuItem>

                  <MenuItem
                    disableRipple
                    onClick={() => {
                      if (!showPdf) {
                        validateAndRun(validateForm, setTouched, () => generatePdfDocument('EN'));
                      }
                    }}
                  >
                    {!showPdf && (
                      <>
                        <PictureAsPdfIcon />

                        <Typography>{t('general.en')}</Typography>
                      </>
                    )}

                    {showPdf && invoicePdfLang === 'EN' && (
                      <QuotePdf
                        company={company}
                        account={account}
                        values={values}
                        logo={logo}
                        update
                        setShowPdf={setShowPdf}
                        showPdf={showPdf}
                        invoicePdfLang={invoicePdfLang}
                        checked={checked}
                        checkedEntries={checkedEntries}
                        freeAppMssg={freeAppMssg}
                      />
                    )}
                  </MenuItem>
                </MenuStyled>
              </Form>
            );
          }}
        </Formik>
      </Container>

      {/* Subscription and relation warnings */}

      <Popover
        open={Boolean(clientLimitAnchor)}
        anchorEl={clientLimitAnchor}
        onClose={closePopovers}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <TypographyPopover>{t('toast.need_to_upgrade_in_order_to_register_new_clients')}</TypographyPopover>
      </Popover>

      <Popover
        open={Boolean(personWarningAnchor)}
        anchorEl={personWarningAnchor}
        onClose={closePopovers}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <TypographyPopover>{t('popover.need_to_select_client_first')}</TypographyPopover>
      </Popover>

      <Popover
        open={Boolean(invoiceLimitAnchor)}
        anchorEl={invoiceLimitAnchor}
        onClose={closePopovers}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <TypographyPopover>{t('toast.need_to_upgrade_in_order_to_register_new_invoices')}</TypographyPopover>
      </Popover>

      {/* Relation/editor popup */}

      {popup && (
        <Popup title={popupTitle}>
          {itemForEdit?.fieldName === 'bill_to' && (
            <Contact addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
          )}

          {itemForEdit?.fieldName === 'person' && valuesRef.current?.bill_to && (
            <Person
              addOrEdit={addOrEditAutoField}
              deleteAutoField={deleteAutoField}
              bill_to={valuesRef.current.bill_to}
            />
          )}

          {itemForEdit?.fieldName === 'payment_term' && (
            <TermOfPayment addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
          )}

          {itemForEdit?.fieldName === 'iteminv_invs' && (
            <ItemInv addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
          )}

          {itemForEdit?.fieldName === 'contract_terms' && (
            <ContractTerm addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
          )}

          {itemForEdit?.fieldName === 'email' && (
            <EmailBasic
              values={valuesRef.current}
              invoicePdfLang={invoicePdfLang}
              checked={checked}
              checkedEntries={checkedEntries}
              freeAppMssg={freeAppMssg}
            />
          )}
        </Popup>
      )}

      {/* Convert quote to invoice */}

      {popupInv2 && invoiceDraft && (
        <PopupInv2 title={`${t('inv.invoice_hash')} ${invoiceDraft.vn ?? ''}`}>
          <Inv initialValue={invoiceDraft} />
        </PopupInv2>
      )}
    </>
  );
});

Quote.displayName = 'Quote';

export default Quote;
